import { MenuDataItem } from '@ant-design/pro-components';
import {
  ArrowUp,
  Building2,
  Flag,
  LayoutDashboard,
  UserPlus,
  Link as LinkIcon,
  Settings,
  StickyNote,
  Upload
} from 'lucide-react';
import {
  CodeOutlined,
  CloudServerOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Badge } from 'antd';
import CVE from '../../assets/icons/cve.svg';

import type { DelegableSetting } from '../../service/delegation.service';

export type MenuLevel = 'workspace' | 'organisation' | 'entity';

export interface MenuOptions {
  nodeId?: number;
  hasCveView: boolean;
  hasSettingsView: boolean;
  /** MEMBERSHIP read in the workspace: gates the workspace Access item. */
  hasAccessView?: boolean;
  upTo?: { name: string; path: string };
  /** Console entries that depend on a capability the caller holds. */
  consoleExtras?: {
    delegation?: boolean;
    /** "IAM & Access" or "Access", by where the caller's own role sits; see `accessLabel`. */
    accessLabel?: string;
    /** Accounts the Users table lists; undefined while loading or on error, so no count is drawn. */
    usersCount?: number;
    /** Settings an entity console shows because they are delegated to it. An org console shows all. */
    settings?: Partial<Record<DelegableSetting, boolean>>;
  };
}

const workspaceItems = (opts: MenuOptions): any[] => [
  {
    key: 'dashboard',
    icon: <LayoutDashboard size={15} />,
    name: 'Dashboard',
    path: '/'
  }, {
    key: 'code-security',
    icon: <CodeOutlined />,
    name: 'Code Security',
    path: '/security/code/findings',
    routes: [
      {
        key: 'code-repositories',
        name: 'Repositories',
        path: '/security/code/repositories'
      }, {
        key: 'code-findings',
        name: 'Findings',
        path: '/security/code/findings'
      }, {
        key: 'code-quality',
        name: 'Quality Diagnostics',
        path: '/security/code/quality'
      }
    ]
  }, {
    key: 'infrastructure-security',
    icon: <CloudServerOutlined />,
    name: 'Infrastructure Security',
    path: '/security/infrastructure/findings',
    routes: [
      {
        key: 'infra-assets',
        name: 'Assets',
        path: '/security/infrastructure/assets'
      }, {
        key: 'infra-findings',
        name: 'Findings',
        path: '/security/infrastructure/findings'
      }
    ]
  }, {
    key: 'r-applications',
    icon: <LayoutDashboard size={15} />,
    name: 'Applications',
    path: '/remediation/applications'
  }, {
    key: 'r-reports',
    icon: <Flag size={15} />,
    name: 'Reports',
    path: '/remediation/reports',
    hidden: true
  }, {
    key: 'r-projects',
    icon: <StickyNote size={15} />,
    name: 'Projects',
    path: '/remediation/projects',
    hidden: true
  }, {
    key: 'data-ingestion',
    icon: <Upload size={15} />,
    name: 'Data Ingestion',
    // Keep the destination discoverable while workspace capabilities are loading.
    // The pages and APIs retain their own authorization checks.
    path: '/connectors',
    routes: [
      {
        key: 'connector',
        icon: <LinkIcon size={15} />,
        name: 'Connectors',
        path: '/connectors',
        hidden: false
      }, {
        key: 'imports',
        icon: <Upload size={15} />,
        name: 'Import Reports',
        path: '/imports',
        hidden: false
      }, {
        key: 'unmapped-findings',
        icon: <WarningOutlined />,
        name: 'Unmapped Findings',
        path: '/imports/unmapped',
        hidden: false
      }
    ]
  }, { key: 'divider-adv', type: 'divider' }, {
    key: 'cve',
    icon: <img src={CVE} width={20} />,
    name: 'CVE',
    path: '/vulnerability/cve',
    hidden: !opts.hasCveView
  }, {
    key: 'access',
    icon: <UserPlus size={15} />,
    name: opts.consoleExtras?.accessLabel ?? 'Access',
    path: '/access',
    hidden: opts.hasAccessView === false
  }, {
    key: 'settings',
    icon: <Settings size={15} />,
    name: 'Settings',
    path: '/settings',
    hidden: !opts.hasSettingsView
  }
];

const consoleItems = (
  base: string,
  childLabel: string,
  childSlug: string,
  extras: MenuOptions['consoleExtras'] = {}
): any[] => [
  {
    key: 'console-overview',
    icon: <LayoutDashboard size={15} />,
    name: 'Overview',
    path: `${base}/overview`
  }, {
    key: `console-${childSlug}`,
    icon: <Building2 size={15} />,
    name: childLabel,
    path: `${base}/${childSlug}`
  }, {
    key: 'console-settings',
    icon: <Settings size={15} />,
    name: 'Settings',
    // General always lists; the sections inside decide what else the console shows.
    path: `${base}/settings`
  }, {
    key: 'console-access',
    icon: <UserPlus size={15} />,
    name: extras.usersCount == null ? (extras.accessLabel ?? 'IAM & Access') : (
      <span className="flex items-center gap-2">
        {extras.accessLabel ?? 'IAM & Access'}
        <Badge
          count={extras.usersCount}
          showZero
          overflowCount={9999}
          color="#f0f0f0"
          style={{
            color: '#595959',
            boxShadow: 'none'
          }}
        />
      </span>
    ),
    // Always listed: delegation only decides who may manage users, not who may see them.
    path: `${base}/access`
  }
];

export const buildMenu = (level: MenuLevel, opts: MenuOptions): MenuDataItem[] => {
  let raw: any[];
  if (level === 'organisation') {
    raw = consoleItems(`/org/${opts.nodeId}`, 'Entities', 'entities', opts.consoleExtras);
  } else if (level === 'entity') {
    raw = consoleItems(`/entity/${opts.nodeId}`, 'Workspaces', 'workspaces', opts.consoleExtras);
  } else {
    raw = workspaceItems(opts);
    if (opts.upTo) {
      raw.unshift({
        key: 'up-to',
        icon: <ArrowUp size={15} />,
        name: `Up to ${opts.upTo.name}`,
        path: opts.upTo.path
      });
    }
  }

  /* keep every visible item; dividers pass through untouched */
  return raw
    .filter((item) => !item.hidden)
    .map((item) =>
      item.type === 'divider'
        ? { type: 'divider' } // AntD needs ONLY this prop
        : {
          key: item.key,
          name: item.name,
          path: item.path,
          icon: item.icon as any,
          alert: item.alert,
          routes: item.routes?.filter((child: any) => !child.hidden)
        }
    );
};
